import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    NavLink,
} from 'react-router-dom';
import {
    Badge,
    Card,
    Col,
    Container,
    Row,
} from 'react-bootstrap';
import {
    FaEdit,
} from 'react-icons/fa';

import API from '../../api/axios';

import SmartTable from '../../components/Smart/SmartTable';
import SmartTopTool from '../../components/Smart/SmartTopTool';
import SmartPagination from '../../components/Smart/SmartPagination';

import {
    useFormat,
} from '../../components/FormatContext';

import useCompanyContext from '../../hooks/useCompanyContext';

const DEFAULT_PAGE_SIZE = 10;

const INITIAL_PAGE = {
    page_number: 1,
    total_pages: 1,
};

const INITIAL_META = {
    total: 0,
    page: 1,
    pageSize:
        DEFAULT_PAGE_SIZE,
    totalPages: 1,
};

const INITIAL_FILTERS = {
    status: [],
    requestType: [],
    serviceCategory: [],
    visibility: [],
};

const FILTER_OPTIONS = {
    status: [
        'OPEN',
        'IN_PROGRESS',
        'WAITING_USER',
        'RESOLVED',
        'CLOSED',
        'CANCELED',
    ],

    requestType: [
        'SERVICE_INQUIRY',
        'RESERVATION_MANAGEMENT',
        'ISSUE_INCIDENT',
    ],

    serviceCategory: [
        'RENTAL',
        'TRIPS',
        'GROUPS',
        'LEASING',
        'OTHERS',
    ],
};

const prettyEnum = (
    value,
) => {
    const raw =
        String(
            value || '',
        ).trim();

    if (!raw) {
        return '-';
    }

    return raw
        .toLowerCase()
        .split('_')
        .map(
            (word) =>
                word
                    .charAt(0)
                    .toUpperCase() +
                word.slice(1),
        )
        .join(' ');
};

const prettyLanguage = (
    language,
) => {
    const map = {
        en: 'English',
        zh: 'Chinese',
        es: 'Spanish',
        fr: 'French',
        de: 'German',
        ja: 'Japanese',
        ko: 'Korean',
    };

    if (!language) {
        return '-';
    }

    return (
        map[
        String(
            language,
        ).toLowerCase()
        ] ||
        String(
            language,
        ).toUpperCase()
    );
};

const getStatusUi = (
    status,
) => {
    const map = {
        OPEN: {
            label: 'Open',
            className:
                'bg-warning-subtle text-warning-emphasis border border-warning-subtle',
        },

        IN_PROGRESS: {
            label:
                'In progress',
            className:
                'bg-info-subtle text-info-emphasis border border-info-subtle',
        },

        WAITING_USER: {
            label:
                'Waiting for customer',
            className:
                'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle',
        },

        RESOLVED: {
            label:
                'Resolved',
            className:
                'bg-success-subtle text-success-emphasis border border-success-subtle',
        },

        CLOSED: {
            label:
                'Closed',
            className:
                'bg-dark-subtle text-dark-emphasis border',
        },

        CANCELED: {
            label:
                'Canceled',
            className:
                'bg-danger-subtle text-danger-emphasis border border-danger-subtle',
        },
    };

    return (
        map[status] || {
            label:
                prettyEnum(status),

            className:
                'bg-light text-dark border',
        }
    );
};

const normalizeRows = (
    items,
) =>
    (items || []).map(
        (item) => ({
            id:
                item?.id,

            serviceCategory:
                item?.serviceCategory,

            requestType:
                item?.requestType,

            status:
                item?.status,

            contactFirstName:
                item?.contactFirstName ??
                '',

            contactLastName:
                item?.contactLastName ??
                '',

            contactEmail:
                item?.contactEmail ??
                '',

            contactPhoneCountry:
                item?.contactPhoneCountry ??
                '',

            contactPhoneNumber:
                item?.contactPhoneNumber ??
                '',

            contactCountry:
                item?.contactCountry ??
                '',

            preferredLanguage:
                item?.preferredLanguage ??
                '',

            place:
                item?.place ??
                '',

            partySize:
                item?.partySize ??
                0,

            plannedFrom:
                item?.plannedFrom,

            plannedTo:
                item?.plannedTo,

            createdAt:
                item?.createdAt,

            updatedAt:
                item?.updatedAt,

            entityType:
                item?.entityType ??
                '',

            entityId:
                item?.entityId ??
                '',

            needReply:
                Boolean(
                    item?.needReply,
                ),
        }),
    );

const Requests = () => {
    const {
        baseFormatters,
    } = useFormat();

    const {
        isAdmin,
        currentCompanyID,
        hasSpecificCompany,
        buildCompanyUrl,
    } = useCompanyContext();

    const [page, setPage] =
        useState(
            INITIAL_PAGE,
        );

    const [
        searchConditions,
        setSearchConditions,
    ] = useState([]);

    const [
        filters,
        setFilters,
    ] = useState(
        INITIAL_FILTERS,
    );

    const [
        requests,
        setRequests,
    ] = useState([]);

    const [meta, setMeta] =
        useState(
            INITIAL_META,
        );

    const [
        loading,
        setLoading,
    ] = useState(false);

    const [
        errorMsg,
        setErrorMsg,
    ] = useState('');

    const requestRef =
        useRef(0);

    const companyScopeKey =
        hasSpecificCompany
            ? String(
                currentCompanyID,
            )
            : 'ALL';

    const previousCompanyScopeRef =
        useRef(
            companyScopeKey,
        );

    const searchType =
        useMemo(() => {
            const base = [
                {
                    key: 'id',
                    label:
                        'Request ID',
                },
                {
                    key:
                        'contactFirstName',
                    label:
                        'Customer first name',
                },
                {
                    key:
                        'contactLastName',
                    label:
                        'Customer last name',
                },
                {
                    key: 'place',
                    label: 'Place',
                },
            ];

            if (isAdmin) {
                base.splice(
                    1,
                    0,
                    {
                        key:
                            'contactEmail',
                        label:
                            'Customer email',
                    },
                    {
                        key:
                            'contactPhoneNumber',
                        label:
                            'Customer phone',
                    },
                );

                base.push({
                    key:
                        'entityId',
                    label:
                        'Entity ID',
                });
            }

            return base;
        }, [isAdmin]);

    const fetchRequests =
        useCallback(async () => {
            const companyChanged =
                previousCompanyScopeRef
                    .current !==
                companyScopeKey;

            previousCompanyScopeRef.current =
                companyScopeKey;

            const requestedPage =
                companyChanged
                    ? 1
                    : page.page_number ||
                    1;

            const requestId =
                ++requestRef.current;

            try {
                setLoading(true);
                setErrorMsg('');

                const params =
                    new URLSearchParams();

                params.set(
                    'page',
                    String(
                        requestedPage,
                    ),
                );

                /*
                 * Request API 原代码已经明确支持 pageSize，
                 * 这里继续保留。
                 */
                params.set(
                    'pageSize',
                    String(
                        meta.pageSize ||
                        DEFAULT_PAGE_SIZE,
                    ),
                );

                if (
                    hasSpecificCompany
                ) {
                    params.set(
                        'supplierId',
                        String(
                            currentCompanyID,
                        ),
                    );
                }

                searchConditions.forEach(
                    (condition) => {
                        const value =
                            (
                                condition?.value ??
                                condition?.keyword ??
                                condition?.search ??
                                condition?.text ??
                                ''
                            )
                                .toString()
                                .trim();

                        if (value) {
                            params.append(
                                'q',
                                value,
                            );
                        }
                    },
                );

                filters.status.forEach(
                    (status) => {
                        if (status) {
                            params.append(
                                'status',
                                String(status),
                            );
                        }
                    },
                );

                filters.requestType.forEach(
                    (type) => {
                        if (type) {
                            params.append(
                                'requestType',
                                String(type),
                            );
                        }
                    },
                );

                filters.serviceCategory.forEach(
                    (category) => {
                        if (category) {
                            params.append(
                                'serviceCategory',
                                String(category),
                            );
                        }
                    },
                );

                const queryString =
                    params.toString();

                const {
                    data,
                } =
                    await API.get(
                        queryString
                            ? `/request?${queryString}`
                            : '/request',
                    );

                if (
                    requestId !==
                    requestRef.current
                ) {
                    return;
                }

                const items =
                    data?.items ??
                    data?.data ??
                    [];

                const nextMeta =
                    data?.meta ??
                    (
                        data?.total !==
                            undefined
                            ? {
                                total:
                                    data.total,

                                page:
                                    data.page ??
                                    requestedPage,

                                pageSize:
                                    data.pageSize ??
                                    meta.pageSize ??
                                    DEFAULT_PAGE_SIZE,

                                totalPages:
                                    Math.max(
                                        1,
                                        Math.ceil(
                                            (
                                                data.total ??
                                                0
                                            ) /
                                            (
                                                data.pageSize ??
                                                meta.pageSize ??
                                                DEFAULT_PAGE_SIZE
                                            ),
                                        ),
                                    ),
                            }
                            : INITIAL_META
                    );

                setRequests(
                    normalizeRows(
                        items,
                    ),
                );

                setMeta(
                    nextMeta,
                );

                const nextPage =
                    nextMeta.page ||
                    1;

                const nextTotalPages =
                    nextMeta.totalPages ||
                    1;

                setPage((prev) => {
                    if (
                        prev.page_number ===
                        nextPage &&
                        prev.total_pages ===
                        nextTotalPages
                    ) {
                        return prev;
                    }

                    return {
                        ...prev,

                        page_number:
                            nextPage,

                        total_pages:
                            nextTotalPages,
                    };
                });
            } catch (error) {
                if (
                    requestId !==
                    requestRef.current
                ) {
                    return;
                }

                setErrorMsg(
                    error?.response?.data
                        ?.message ||
                    error?.message ||
                    'Failed to load requests',
                );

                setRequests([]);

                setMeta(
                    INITIAL_META,
                );

                setPage(
                    (prev) => ({
                        ...prev,
                        total_pages: 1,
                    }),
                );
            } finally {
                if (
                    requestId ===
                    requestRef.current
                ) {
                    setLoading(false);
                }
            }
        }, [
            companyScopeKey,
            currentCompanyID,
            hasSpecificCompany,
            page.page_number,
            meta.pageSize,
            searchConditions,
            filters.status,
            filters.requestType,
            filters.serviceCategory,
        ]);

    const handleSetSearchConditions =
        useCallback(
            (
                valueOrUpdater,
            ) => {
                setSearchConditions(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage(
                    (prev) =>
                        prev.page_number ===
                            1
                            ? prev
                            : {
                                ...prev,
                                page_number:
                                    1,
                            },
                );
            },
            [],
        );

    const handleSetFilters =
        useCallback(
            (
                valueOrUpdater,
            ) => {
                setFilters(
                    (prev) =>
                        typeof valueOrUpdater ===
                            'function'
                            ? valueOrUpdater(
                                prev,
                            )
                            : valueOrUpdater,
                );

                setPage(
                    (prev) =>
                        prev.page_number ===
                            1
                            ? prev
                            : {
                                ...prev,
                                page_number:
                                    1,
                            },
                );
            },
            [],
        );

    useEffect(() => {
        fetchRequests();
    }, [fetchRequests]);

    const columns =
        useMemo(
            () => [
                {
                    keys: [
                        'requestType',
                        'serviceCategory',
                        'createdAt',
                        'entityType',
                        'entityId',
                    ],

                    label:
                        'Request',

                    render:
                        (item) => (
                            <div className="d-flex flex-column gap-2 py-1">
                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                    <span className="fw-semibold text-dark">
                                        {prettyEnum(
                                            item.requestType,
                                        )}
                                    </span>

                                    <Badge
                                        bg="light"
                                        text="dark"
                                        pill
                                        className="fw-normal"
                                    >
                                        {prettyEnum(
                                            item.serviceCategory,
                                        )}
                                    </Badge>

                                    {isAdmin &&
                                        (
                                            item.entityType ||
                                            item.entityId
                                        ) && (
                                            <Badge
                                                bg="light"
                                                text="secondary"
                                                className="border fw-normal"
                                            >
                                                {item.entityType
                                                    ? prettyEnum(
                                                        item.entityType,
                                                    )
                                                    : 'Entity'}{' '}
                                                #
                                                {item.entityId ||
                                                    '-'}
                                            </Badge>
                                        )}
                                </div>

                                <div className="small text-muted">
                                    Submitted{' '}
                                    {item.createdAt
                                        ? baseFormatters.datetime.format(
                                            new Date(
                                                item.createdAt,
                                            ),
                                        )
                                        : '-'}
                                </div>
                            </div>
                        ),
                },

                {
                    keys: [
                        'contactFirstName',
                        'contactLastName',
                        'contactEmail',
                        'contactPhoneCountry',
                        'contactPhoneNumber',
                        'contactCountry',
                        'preferredLanguage',
                    ],

                    label:
                        'Customer',

                    render:
                        (item) => (
                            <div className="d-flex flex-column gap-2 py-1">
                                <div className="fw-semibold text-dark">
                                    {[
                                        item.contactFirstName,
                                        item.contactLastName,
                                    ]
                                        .filter(
                                            Boolean,
                                        )
                                        .join(
                                            ' ',
                                        ) ||
                                        '-'}
                                </div>

                                <div className="d-flex flex-wrap gap-2">
                                    {item.contactCountry && (
                                        <Badge
                                            bg="light"
                                            text="dark"
                                            pill
                                            className="fw-normal"
                                        >
                                            {
                                                item.contactCountry
                                            }
                                        </Badge>
                                    )}

                                    {item.preferredLanguage && (
                                        <Badge
                                            bg="light"
                                            text="dark"
                                            pill
                                            className="fw-normal"
                                        >
                                            {prettyLanguage(
                                                item.preferredLanguage,
                                            )}
                                        </Badge>
                                    )}
                                </div>

                                {isAdmin && (
                                    <div className="small text-muted d-flex flex-column gap-1">
                                        <span>
                                            {item.contactEmail ||
                                                'No email provided'}
                                        </span>

                                        <span>
                                            {[
                                                item.contactPhoneCountry,
                                                item.contactPhoneNumber,
                                            ]
                                                .filter(
                                                    Boolean,
                                                )
                                                .join(
                                                    ' ',
                                                ) ||
                                                'No phone provided'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        ),
                },

                {
                    keys: [
                        'place',
                        'plannedFrom',
                        'plannedTo',
                        'partySize',
                    ],

                    label:
                        'Trip details',

                    render:
                        (item) => (
                            <div className="d-flex flex-column gap-2 py-1">
                                <div className="fw-semibold text-dark">
                                    {item.place ||
                                        'Destination not specified'}
                                </div>

                                <div className="small text-muted">
                                    {item.plannedFrom
                                        ? baseFormatters.date.medium.format(
                                            new Date(
                                                item.plannedFrom,
                                            ),
                                        )
                                        : '-'}

                                    {' — '}

                                    {item.plannedTo
                                        ? baseFormatters.date.medium.format(
                                            new Date(
                                                item.plannedTo,
                                            ),
                                        )
                                        : '-'}
                                </div>

                                <div className="small">
                                    <span className="text-muted">
                                        Travelers:{' '}
                                    </span>

                                    <span className="fw-medium">
                                        {item.partySize ||
                                            0}
                                    </span>
                                </div>
                            </div>
                        ),
                },

                {
                    keys: [
                        'status',
                        'updatedAt',
                        'needReply',
                    ],

                    label:
                        'Progress',

                    render:
                        (item) => {
                            const statusUi =
                                getStatusUi(
                                    item.status,
                                );

                            return (
                                <div className="d-flex flex-column gap-2 py-1">
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span
                                            className={`px-2 py-1 rounded-pill small fw-medium ${statusUi.className}`}
                                        >
                                            {
                                                statusUi.label
                                            }
                                        </span>

                                        {item.needReply && (
                                            <Badge
                                                bg="danger"
                                                pill
                                                className="fw-normal"
                                            >
                                                Need reply
                                            </Badge>
                                        )}
                                    </div>

                                    <div className="small text-muted">
                                        Last updated{' '}
                                        {item.updatedAt
                                            ? baseFormatters.datetime.format(
                                                new Date(
                                                    item.updatedAt,
                                                ),
                                            )
                                            : '-'}
                                    </div>
                                </div>
                            );
                        },
                },

                {
                    key:
                        'actions',

                    label:
                        'Actions',

                    render:
                        (item) => (
                            <div className="d-flex align-items-center py-1">
                                <NavLink
                                    to={buildCompanyUrl(
                                        `/requests/edit/${item.id}`,
                                    )}
                                    className="btn btn-sm btn-outline-primary rounded-pill px-3"
                                    title="Open request"
                                >
                                    <FaEdit
                                        size={14}
                                        className="me-1"
                                    />

                                    Open
                                </NavLink>
                            </div>
                        ),
                },
            ],
            [
                baseFormatters.datetime,
                baseFormatters.date,
                isAdmin,
                buildCompanyUrl,
            ],
        );

    return (
        <Container
            fluid
            className="d-flex flex-column"
        >
            <Row className="mb-4">
                <Col sm={8}>
                    <span className="fw-semibold">
                        Requests List
                    </span>

                    <span className="text-muted ms-2 small">
                        Total:{' '}
                        {meta.total}
                    </span>
                </Col>

                <Col
                    sm={4}
                    className="text-end"
                >
                    <span className="fw-semibold">
                        <NavLink
                            to={buildCompanyUrl(
                                '/dashboard',
                            )}
                            className="block-link"
                        >
                            Dashboard
                        </NavLink>

                        {' - Requests List'}
                    </span>
                </Col>
            </Row>

            <Row>
                <Col sm={12}>
                    <Card className="h-100 p-2">
                        <Card.Body className="px-2 pt-2 pb-5">
                            <Row>
                                <Col md={12}>
                                    <SmartTopTool
                                        searchType={
                                            searchType
                                        }
                                        searchConditions={
                                            searchConditions
                                        }
                                        setSearchConditions={
                                            handleSetSearchConditions
                                        }
                                        filters={
                                            filters
                                        }
                                        filterOptions={
                                            FILTER_OPTIONS
                                        }
                                        setFilters={
                                            handleSetFilters
                                        }
                                    />
                                </Col>

                                {errorMsg && (
                                    <Col
                                        md={12}
                                        className="mt-2"
                                    >
                                        <div className="alert alert-danger mb-2">
                                            {
                                                errorMsg
                                            }
                                        </div>
                                    </Col>
                                )}

                                <Col md={12}>
                                    <SmartTable
                                        data={
                                            requests
                                        }
                                        columns={
                                            columns
                                        }
                                        loading={
                                            loading
                                        }
                                        emptyState={
                                            <div className="text-center py-5 text-muted">
                                                <p>
                                                    {errorMsg ||
                                                        (
                                                            loading
                                                                ? 'Loading...'
                                                                : 'No matching requests found'
                                                        )}
                                                </p>
                                            </div>
                                        }
                                    />
                                </Col>

                                <Col
                                    md={12}
                                    className="d-flex justify-content-end"
                                >
                                    <SmartPagination
                                        page={
                                            page
                                        }
                                        setPage={
                                            setPage
                                        }
                                    />
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
};

export default Requests;